import { useMemo } from 'react';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import {
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import { AppAuthProvider } from './auth/ClerkProvider';
import { router } from './router';
import { useUiStore } from './stores/ui.store';
import { toast } from './stores/toast.store';
import { ToastContainer } from './components/ToastContainer';

const mutationCache = new MutationCache({
  onSuccess: (_data, _variables, _context, mutation) => {
    if (mutation.meta?.silent) return;
    const message =
      (mutation.meta?.successMessage as string | undefined) ??
      'Saved successfully';
    toast.success(message);
  },
  onError: (error, _variables, _context, mutation) => {
    if (mutation.meta?.silent) return;
    const message =
      (mutation.meta?.errorMessage as string | undefined) ??
      error?.message ??
      'Something went wrong';
    toast.error(message);
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

function ThemedApp() {
  const themeMode = useUiStore((s) => s.themeMode);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const resolvedMode =
    themeMode === 'system'
      ? prefersDark
        ? 'dark'
        : 'light'
      : themeMode === 'high-contrast'
        ? 'light'
        : themeMode;

  const theme = useMemo(() => {
    if (themeMode === 'high-contrast') {
      return createTheme({
        palette: {
          mode: 'light',
          primary: { main: '#000000' },
          background: { default: '#ffffff', paper: '#ffffff' },
          text: { primary: '#000000', secondary: '#333333' },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: { border: '2px solid #000' },
            },
          },
        },
      });
    }

    return createTheme({
      palette: {
        mode: resolvedMode,
        ...(resolvedMode === 'dark' && {
          background: { default: '#121212', paper: '#1e1e1e' },
        }),
      },
    });
  }, [resolvedMode, themeMode]);

  const showDevtools = import.meta.env.VITE_ENABLE_DEVTOOLS === 'true';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RouterProvider router={router} />
      <ToastContainer />
      {showDevtools && <ReactQueryDevtools initialIsOpen={false} />}
    </ThemeProvider>
  );
}

export function App() {
  return (
    <AppAuthProvider>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
      </QueryClientProvider>
    </AppAuthProvider>
  );
}

export default App;
